import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  alpha,
  Alert,
  CircularProgress,
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import SaveIcon from '@mui/icons-material/Save';
import { motion } from 'framer-motion';
import api from '../../services/api';

// ─── Design tokens — cyberpunk dark system ────────────────────────────────────
const B = {
  700: '#1D4ED8',
  900: '#1E3A8A',
};

const NEON  = '#38BDF8';
const NEON2 = '#818CF8';
const NEON3 = '#34D399';

const SURFACE_BASE = '#050d1a';
const SURFACE_MID  = '#0a1628';
const SURFACE_UP   = '#0f1f38';

const DOT_GRID   = `url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Ccircle cx='10' cy='10' r='0.55' fill='%2338BDF8' fill-opacity='0.07'/%3E%3C/svg%3E")`;
const SCAN_LINES = `repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(14,165,233,0.016) 3px, rgba(14,165,233,0.016) 4px)`;

const VIEW_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600&family=Rajdhani:wght@500;600;700&family=DM+Sans:wght@300;400;500;600&display=swap');
  @keyframes form-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.35; } }
  @keyframes neon-ping  { 0% { transform: scale(0.8); opacity: 0.9; } 100% { transform: scale(2.2); opacity: 0; } }
  @keyframes bar-sweep  { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }
`;

const MotionBox = motion(Box);

const defaultRows = (() => {
  const viewOnly = 'All authenticated users';
  const sectionRole = 'Visible by sidebar role rule';
  const inheritedCrud = 'No explicit route guard; follows page-level checks';
  return [
    { view: 'Dashboard',        create: '-',              read: viewOnly,       updateDelete: '-' },
    { view: 'UserList',         create: 'Admin / HR / Team Leader',  read: viewOnly,       updateDelete: 'Admin / HR / Team Leader (self edit allowed)' },
    { view: 'User Form',        create: 'Admin / HR / Team Leader',  read: 'Admin / HR / Team Leader', updateDelete: 'Admin / HR / Team Leader + self profile edit' },
    { view: 'User Details',     create: '-',              read: viewOnly,       updateDelete: 'Admin / HR / Team Leader + self profile edit' },
    { view: 'Profile',          create: '-',              read: viewOnly,       updateDelete: 'Own profile' },
    { view: 'Attendance',       create: 'Team Leader / HR (manual records)', read: viewOnly, updateDelete: 'Team Leader / HR (manual update)' },
    { view: 'Tasks',            create: 'Team Leader or Engineering HOD', read: 'Team Leader: all; others: department-scoped', updateDelete: 'Team Leader or Engineering HOD (department-scoped)' },
    { view: 'Calendar',         create: '-',              read: viewOnly,       updateDelete: '-' },
    { view: 'Events',           create: viewOnly,         read: viewOnly,       updateDelete: 'Owner or Team Leader' },
    { view: 'Penalties',        create: 'HR / Team Leader', read: 'HR / Team Leader / assigner / assignee', updateDelete: 'HR / Team Leader / assigner' },
    { view: 'Gallery',          create: 'Team Leader or Multimedia bureau', read: viewOnly, updateDelete: 'Team Leader or Multimedia bureau' },
    { view: 'Management',       create: 'Admin only',     read: 'Admin only',   updateDelete: 'Admin only' },
    { view: 'Bureau',           create: 'Admin only',     read: 'Admin only',   updateDelete: 'Admin only' },
    { view: 'Department',       create: 'Admin only',     read: 'Admin only',   updateDelete: 'Admin only' },
    { view: 'Companies',        create: sectionRole,      read: sectionRole,    updateDelete: sectionRole },
    { view: 'Partners',         create: sectionRole,      read: sectionRole,    updateDelete: sectionRole },
    { view: 'Requirements',     create: inheritedCrud,    read: viewOnly,       updateDelete: inheritedCrud },
    { view: 'Inventory',        create: 'Team Leader or Inventory bureau', read: 'Team Leader or Inventory bureau', updateDelete: 'Team Leader or Inventory bureau' },
    { view: 'Borrow',           create: 'Team Leader or Inventory bureau', read: 'Team Leader or Inventory bureau', updateDelete: 'Team Leader or Inventory bureau' },
    { view: 'Claims',           create: viewOnly,         read: viewOnly,       updateDelete: 'Owner or Team Leader / Treasurer' },
    { view: 'Budget',           create: 'Team Leader or Treasurer', read: 'Team Leader or Treasurer', updateDelete: 'Team Leader or Treasurer' },
    { view: 'Budget Expenses',  create: 'Team Leader or Treasurer', read: 'Team Leader or Treasurer', updateDelete: 'Team Leader or Treasurer' },
    { view: 'Analysis',         create: 'Team Leader or Game Management (bureau 2)', read: viewOnly, updateDelete: 'Team Leader or Game Management; members: public analyses only' },
    { view: 'Documents',        create: 'Team Leader or Secretary', read: viewOnly, updateDelete: 'Team Leader or Secretary (members: public docs only)' },
    { view: 'Maintenance',      create: viewOnly,         read: viewOnly,       updateDelete: 'Team Leader, Maintenance bureau, or reporter' },
    { view: 'Parcels',          create: viewOnly,         read: viewOnly,       updateDelete: 'Record owner, Team Leader, or Purchasing bureau' },
  ];
})();

// ─── Role colour map ──────────────────────────────────────────────────────────
const getRoleStyle = (text) => {
  if (text === '-') return { color: alpha(NEON, 0.25), fontStyle: 'italic' };
  if (text.includes('Admin only') || text === 'Admin only') return { color: '#F87171' };
  if (text.includes('Admin') && text.includes('HR')) return { color: '#FCD34D' };
  if (text.includes('All authenticated') || text.includes('viewOnly') || text === 'All authenticated users') return { color: NEON3 };
  if (text.includes('owner') || text.includes('Own')) return { color: NEON2 };
  if (text.includes('sidebar')) return { color: NEON };
  if (text.includes('Inherited') || text.includes('component')) return { color: alpha(NEON, 0.55) };
  return { color: alpha('#E2E8F0', 0.7) };
};

const AccessibilityView = () => {
  const [rows, setRows] = useState(defaultRows);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    const cssId = 'accessview-ds-css';
    if (!document.getElementById(cssId)) {
      const el = document.createElement('style');
      el.id = cssId; el.innerHTML = VIEW_CSS;
      document.head.appendChild(el);
    }
  }, []);
  useEffect(() => {
    const loadTemplate = async () => {
      try {
        setLoading(true);
        setError('');
        const response = await api.get('/accessibility-template', { cache: false });
        if (Array.isArray(response?.data?.rows) && response.data.rows.length > 0) {
          setRows(response.data.rows);
        }
      } catch (err) {
        setError('Failed to load saved template. Showing default template.');
      } finally {
        setLoading(false);
      }
    };
    loadTemplate();
  }, []);

  const changeCell = (index, field, value) => {
    setRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)),
    );
    setDirty(true);
    setSuccess('');
  };

  const hasInvalidRows = useMemo(
    () => rows.some((row) => !row.view?.trim() || !row.create?.trim() || !row.read?.trim() || !row.updateDelete?.trim()),
    [rows],
  );

  const saveTemplate = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      await api.put('/accessibility-template', { rows });
      setSuccess('Permission template updated successfully.');
      setDirty(false);
    } catch (err) {
      const firstValidationError = err.response?.data?.errors
        ? Object.values(err.response.data.errors).flat()?.[0]
        : null;
      setError(firstValidationError || err.response?.data?.message || 'Failed to update accessibility template.');
    } finally {
      setSaving(false);
    }
  };

  const headerCellSx = {
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: '0.65rem', fontWeight: 600, letterSpacing: 1.5,
    textTransform: 'uppercase', color: alpha(NEON, 0.7),
    background: `linear-gradient(135deg, ${alpha(NEON, 0.06)} 0%, ${alpha(SURFACE_MID, 0.98)} 100%)`,
    borderBottom: `1px solid ${alpha(NEON, 0.18)}`,
    py: 1.5, px: 2,
  };

  const bodyCellSx = (text, isView = false) => ({
    fontFamily: isView ? "'Rajdhani', sans-serif" : "'DM Sans', sans-serif",
    fontSize: isView ? '0.85rem' : '0.8rem',
    fontWeight: isView ? 600 : 400,
    letterSpacing: isView ? 0.4 : 0,
    color: isView ? alpha('#E2E8F0', 0.85) : alpha('#E2E8F0', 0.7),
    borderBottom: `1px solid ${alpha(NEON, 0.07)}`,
    py: 1.2, px: 2,
    ...(!isView && getRoleStyle(text)),
  });

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      fontFamily: "'DM Sans', sans-serif",
      fontSize: '0.78rem',
      color: alpha('#E2E8F0', 0.88),
      background: alpha(SURFACE_UP, 0.72),
      '& fieldset': { borderColor: alpha(NEON, 0.2) },
      '&:hover fieldset': { borderColor: alpha(NEON, 0.38) },
      '&.Mui-focused fieldset': { borderColor: alpha(NEON, 0.7), borderWidth: '1.5px' },
    },
    '& .MuiInputBase-input': {
      color: alpha('#E2E8F0', 0.9),
      lineHeight: 1.35,
    },
    '& .MuiInputBase-input::placeholder': {
      color: alpha(NEON, 0.35),
      opacity: 1,
    },
  };

  const legend = [
    { color: NEON3,              label: 'All authenticated' },
    { color: '#F87171',          label: 'Admin only' },
    { color: '#FCD34D',          label: 'Admin / HR / TL' },
    { color: NEON2,              label: 'Owner / self' },
    { color: NEON,               label: 'Sidebar role' },
    { color: alpha(NEON, 0.55), label: 'Inherited / component' },
  ];

  return (
    <Box>
      {/* ── Outer panel shell ─────────────────────────────────────────────── */}
      <Box sx={{
        borderRadius: '14px',
        background: `linear-gradient(160deg, ${alpha(SURFACE_BASE, 0.97)} 0%, ${alpha(SURFACE_MID, 0.95)} 100%)`,
        border: `1px solid ${alpha(NEON, 0.17)}`,
        boxShadow: [
          `0 0 0 1px ${alpha(B[900], 0.9)}`,
          `0 4px 24px ${alpha('#000', 0.45)}`,
          `0 20px 60px ${alpha('#000', 0.3)}`,
          `inset 0 1px 0 ${alpha(NEON, 0.1)}`,
        ].join(', '),
        backdropFilter: 'blur(20px)',
        overflow: 'hidden',
        position: 'relative',
        // Sweeping neon top bar
        '&::before': {
          content: '""', display: 'block', height: '2px',
          background: `linear-gradient(90deg, transparent, ${alpha(NEON, 0.8)} 30%, ${alpha(NEON2, 0.9)} 50%, ${alpha(NEON, 0.8)} 70%, transparent)`,
          backgroundSize: '200% 100%', animation: 'bar-sweep 5s linear infinite',
        },
      }}>
        {/* Textures */}
        <Box sx={{ position: 'absolute', inset: 0, background: DOT_GRID,   pointerEvents: 'none', zIndex: 0 }} />
        <Box sx={{ position: 'absolute', inset: 0, background: SCAN_LINES, pointerEvents: 'none', zIndex: 0, opacity: 0.65 }} />
        <Box sx={{ position: 'absolute', top: -40, right: -40, width: 200, height: 200, borderRadius: '50%',
          background: `radial-gradient(circle, ${alpha(NEON, 0.06)}, transparent 65%)`, pointerEvents: 'none', zIndex: 0 }} />

        {/* ── Card header ─────────────────────────────────────────────────── */}
        <Box sx={{
          px: 3, py: 2, position: 'relative', zIndex: 1,
          display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
          background: `linear-gradient(135deg, ${alpha(NEON, 0.05)} 0%, ${alpha(SURFACE_MID, 0.6)} 100%)`,
          borderBottom: `1px solid ${alpha(NEON, 0.12)}`,
        }}>
          {/* Icon box */}
          <Box sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 36, height: 36, borderRadius: '10px',
            background: `linear-gradient(135deg, ${alpha(NEON, 0.18)}, ${alpha(B[700], 0.5)})`,
            border: `1px solid ${alpha(NEON, 0.3)}`, color: NEON, flexShrink: 0,
            boxShadow: `0 0 14px ${alpha(NEON, 0.3)}, 0 3px 8px ${alpha('#000', 0.4)}`,
          }}>
            <SecurityIcon sx={{ fontSize: 17 }} />
          </Box>

          <Box>
            <Typography sx={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: '1rem', letterSpacing: 0.7, color: alpha('#E2E8F0', 0.9) }}>
              Accessibility View
            </Typography>
            <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.78rem', color: alpha(NEON, 0.45), mt: 0.15 }}>
              Permission matrix for all internal layout views · Admin only
            </Typography>
          </Box>

          {/* Admin badge */}
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Chip
              label="Admin Only"
              size="small"
              sx={{
                height: 22, fontFamily: "'JetBrains Mono', monospace", fontSize: '0.6rem', fontWeight: 600, letterSpacing: 0.8,
                bgcolor: alpha('#F87171', 0.12), color: '#F87171',
                border: `1px solid ${alpha('#F87171', 0.35)}`,
              }}
            />
            {/* Pulse dot */}
            <Box sx={{ position: 'relative', width: 8, height: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: NEON3, boxShadow: `0 0 6px ${NEON3}`, animation: 'form-pulse 2.8s ease-in-out infinite' }} />
              <Box sx={{ position: 'absolute', inset: -3, borderRadius: '50%', border: `1px solid ${alpha(NEON3, 0.3)}`, animation: 'neon-ping 2.8s ease-out infinite' }} />
            </Box>
            <Button
              size="small"
              startIcon={saving ? <CircularProgress size={14} sx={{ color: NEON }} /> : <SaveIcon />}
              onClick={saveTemplate}
              disabled={loading || saving || !dirty || hasInvalidRows}
              sx={{
                ml: 1,
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: '0.66rem',
                letterSpacing: 0.6,
                color: alpha('#E2E8F0', 0.9),
                border: `1px solid ${alpha(NEON, 0.35)}`,
                bgcolor: alpha(NEON, 0.08),
                '&:hover': { bgcolor: alpha(NEON, 0.16) },
              }}
            >
              {saving ? 'Saving...' : 'Save Template'}
            </Button>
          </Box>
        </Box>

        {(error || success) && (
          <Box sx={{ px: 2.5, pt: 1.5, position: 'relative', zIndex: 1 }}>
            {error ? (
              <Alert
                severity="error"
                sx={{
                  mb: success ? 1 : 0,
                  background: alpha('#450a0a', 0.92),
                  border: `1px solid ${alpha('#F87171', 0.35)}`,
                  color: '#FCA5A5',
                }}
              >
                {error}
              </Alert>
            ) : null}
            {success ? (
              <Alert
                severity="success"
                sx={{
                  background: alpha('#052e1a', 0.9),
                  border: `1px solid ${alpha(NEON3, 0.35)}`,
                  color: alpha(NEON3, 0.96),
                }}
              >
                {success}
              </Alert>
            ) : null}
          </Box>
        )}

        {/* ── Table ─────────────────────────────────────────────────────────── */}
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <TableContainer sx={{ maxHeight: '62vh' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {['View', 'Create', 'Read', 'Update / Delete'].map((h) => (
                    <TableCell key={h} sx={{ ...headerCellSx, minWidth: h === 'View' ? 160 : 220 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ py: 4 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        <CircularProgress size={20} />
                        <Typography sx={{ color: alpha('#E2E8F0', 0.7), fontSize: '0.85rem' }}>
                          Loading permission template...
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : null}
                {rows.map((row, i) => (
                  <MotionBox
                    key={row.view}
                    component="tr"
                    initial={{ opacity: 0, x: -6 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.18, delay: i * 0.018 }}
                    sx={{
                      '&:hover td': { background: alpha(NEON, 0.04) },
                      '&:hover': { cursor: 'default' },
                    }}
                  >
                    <TableCell sx={bodyCellSx(row.view, true)}>{row.view}</TableCell>
                    <TableCell sx={bodyCellSx(row.create)}>
                      <TextField
                        fullWidth
                        size="small"
                        value={row.create}
                        onChange={(e) => changeCell(i, 'create', e.target.value)}
                        variant="outlined"
                        multiline
                        minRows={1}
                        maxRows={3}
                        sx={inputSx}
                      />
                    </TableCell>
                    <TableCell sx={bodyCellSx(row.read)}>
                      <TextField
                        fullWidth
                        size="small"
                        value={row.read}
                        onChange={(e) => changeCell(i, 'read', e.target.value)}
                        variant="outlined"
                        multiline
                        minRows={1}
                        maxRows={3}
                        sx={inputSx}
                      />
                    </TableCell>
                    <TableCell sx={bodyCellSx(row.updateDelete)}>
                      <TextField
                        fullWidth
                        size="small"
                        value={row.updateDelete}
                        onChange={(e) => changeCell(i, 'updateDelete', e.target.value)}
                        variant="outlined"
                        multiline
                        minRows={1}
                        maxRows={3}
                        sx={inputSx}
                      />
                    </TableCell>
                  </MotionBox>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* ── Legend footer ─────────────────────────────────────────────────── */}
        <Box sx={{
          px: 3, py: 2, position: 'relative', zIndex: 1,
          borderTop: `1px solid ${alpha(NEON, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(NEON, 0.03)} 0%, ${alpha(SURFACE_MID, 0.5)} 100%)`,
          display: 'flex', flexWrap: 'wrap', gap: 1.2, alignItems: 'center',
        }}>
          <Typography sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '0.58rem', letterSpacing: 1.5, color: alpha(NEON, 0.4), textTransform: 'uppercase', mr: 0.5 }}>
            Legend
          </Typography>
          {legend.map(({ color, label }) => (
            <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
              <Box sx={{ width: 7, height: 7, borderRadius: '50%', background: color, boxShadow: `0 0 5px ${color}`, flexShrink: 0 }} />
              <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.72rem', color: alpha('#E2E8F0', 0.55) }}>{label}</Typography>
            </Box>
          ))}
        </Box>

      </Box>
    </Box>
  );
};

export default AccessibilityView;
